import type { CatalogGpu, Flavor, LeaseRoomGpu } from '@/api/types'
import { DASH, size, sizeMb } from '@/ui/format'
import { FREE } from './words'

// 사양의 낱말(F-62). 자원마다 값이 있으면 고정, 없으면 신청하는 사람이 적는다 —
// 화면은 그 둘을 같은 줄에서 말한다. `—` 는 없는 값이고 `자유` 는 고르는 값이라 다르다.
// 그 낱말의 원본은 `domain/words.ts` 다 — 직위의 한도도 같은 낱말을 쓴다.
//
// 카드는 그 둘 중 어느 쪽도 아니다: 신청하는 사람이 사양과 따로 모델과 장 수를 고른다(F-96).
// 사양은 카드를 갖지 않고, 그 전에 만든 사양만 카드를 말한다 — 신청이 카드를 고르지 않으면 그 카드가 간다.
// 그래서 사양의 카드 칸은 값이거나 `—` 이고 `자유` 가 설 일이 없다.

export function coresText(f: Flavor): string {
  return f.cores == null ? FREE : `${f.cores}코어`
}

export function memoryText(f: Flavor): string {
  return f.memory_mb == null ? FREE : sizeMb(f.memory_mb)
}

export function diskText(f: Flavor): string {
  return f.disk_gb == null ? FREE : size(f.disk_gb)
}

/** 사양이 주는 카드 — `GPU 2장 A16`. 카드가 없는 사양은 없는 값이다. */
export function gpuText(f: Flavor): string {
  return f.gpus == null || f.gpu_model == null ? DASH : `GPU ${f.gpus}장 ${f.gpu_model}`
}

/**
 * 한 줄로 읽는 사양 — `코어 2 · 메모리 8GB · 디스크 128GB`. VM 생성 페이지의 VM 유형 select 가 이름 아래에 이 줄을 둔다.
 * 축의 이름을 값이 지고 간다 — 자유인 자리가 둘이면 `자유 · 자유` 는 무엇이 자유인지 말하지 못한다.
 * 카드가 있으면 그것도 말한다.
 */
export function flavorSpec(f: Flavor): string {
  const parts = [`코어 ${f.cores ?? FREE}`, `메모리 ${memoryText(f)}`, `디스크 ${diskText(f)}`]
  if (f.gpus != null && f.gpu_model != null) parts.push(gpuText(f))
  return parts.join(' · ')
}

/** 그 사양이 신청하는 사람에게 남겨 둔 자원. VM 생성 페이지는 이 칸만 낸다. */
export function freeOf(f: Flavor | undefined) {
  return {
    cores: f != null && f.cores == null,
    memory: f != null && f.memory_mb == null,
    disk: f != null && f.disk_gb == null,
  }
}

/** 카드를 고르는 창이 고를 수 있는 모델 하나와 그 한 장의 메모리. */
export type GpuChoice = { model: string; vramMb: number }

/** 그 모델 n 장이 요구하는 메모리, MB — 백엔드가 견주는 단위다. 신청의 MB 를 그대로 재는 자리가 쓴다. */
export function gpuMemoryFloorMb(vramMb: number, gpus: number): number {
  return vramMb * gpus
}

/** 그 모델 n 장이 요구하는 메모리, GB. 그 카드를 받는 VM 의 메모리는 이 값 이상이어야 한다. */
export function gpuMemoryFloorGb(vramMb: number, gpus: number): number {
  return Math.ceil(gpuMemoryFloorMb(vramMb, gpus) / 1024)
}

/** 카드를 고르는 창이 고를 수 있는 모델 하나 — 한 장의 메모리와 select 의 라벨. */
export type CatalogModel = GpuChoice & { label: string }

/**
 * 사양이 그 하한(MB)의 카드를 받을 수 있는가. 메모리를 고정한 사양은 그 값이 하한 이상이어야 하고,
 * 메모리를 자유로 둔 사양은 신청이 적는 값이 하한을 채우면 되므로 받는다 — 그 칸이 하한을 따로 잰다.
 * 견주는 단위는 백엔드처럼 MB 다.
 */
export function holdsCards(f: Flavor, floorMb: number): boolean {
  return f.memory_mb == null || f.memory_mb >= floorMb
}

/**
 * `/catalog` 의 모델 항목을 카드를 고르는 창(신청·변경 신청·GPU 승인)의 선택지로. 세 창이 같은 모델을 같은 말로 부른다.
 *
 * 그 목록은 이 설치가 카드를 가진 모델이라 **남은 장 수가 0 이어도 온다** — 남은 장 수는 값이지 자격이 아니고,
 * 0 은 `남은 카드 없음` 이라 말한다. 그런 모델을 빼면 쥔 카드가 전부 할당된 모델의 메모리 하한을 창이 재지 못한다.
 * `held`(쥐거나 신청한 모델)가 목록에 없으면 그 이름만 맨 앞에 서고, 한 장의 메모리를 모르므로 창은 하한을 재지 않는다.
 */
export function catalogModels(rows: CatalogGpu[], held?: string): CatalogModel[] {
  const out = rows.map((g) => ({
    model: g.model,
    vramMb: g.vram_mb ?? 0,
    label: g.free > 0 ? `${g.model} · 최대 ${g.free}장` : `${g.model} · 남은 카드 없음`,
  }))
  if (held !== undefined && !out.some((m) => m.model === held)) out.unshift({ model: held, vramMb: 0, label: held || DASH })
  return out
}

/** 변경·수정·승인 창이 고르는 모델 하나 — 한 장의 메모리, 그 노드에 남은 장 수, VM 이 그 모델로 가질 수 있는 장 수(쥔 것 + 남은 것), 그 노드에 있는가. */
export type RoomModel = { model: string; vramMb: number; free: number; max: number; onNode: boolean }

/**
 * 변경·수정·승인 창의 모델 목록. 변경은 VM 이 있는 노드에서만 카드를 받으므로 그 노드의 모델(`leases.room`)이 먼저 서고
 * 남은 장 수도 그 노드의 것이다 — 남은 카드가 없는 모델도 선다(그 노드에 있지만 다 나갔다는 것이 값이다).
 * 다른 노드에만 있는 카탈로그의 모델은 뒤에 서고 가질 수 있는 장 수가 0 이다 — 빼면 사람은 그 모델이 왜 없는지 모른다.
 * `held`(VM 이 쥔 모델)가 어디에도 없으면 이름만 맨 앞에 서고 한 장의 메모리를 모르므로 창은 하한을 재지 않는다.
 */
export function roomModels(rows: LeaseRoomGpu[], catalog: CatalogGpu[], held?: string): RoomModel[] {
  const out: RoomModel[] = rows.map((g) => ({ model: g.model, vramMb: g.vram_mb ?? 0, free: g.free, max: g.held + g.free, onNode: true }))
  for (const g of catalog) {
    if (!out.some((m) => m.model === g.model)) out.push({ model: g.model, vramMb: g.vram_mb ?? 0, free: 0, max: 0, onNode: false })
  }
  if (held !== undefined && held !== '' && !out.some((m) => m.model === held)) out.unshift({ model: held, vramMb: 0, free: 0, max: 0, onNode: true })
  return out
}

/**
 * 모델 하나를 고르는 칸(`components/Choice.tsx`)의 항목으로. 제목은 모델이고 둘째 줄은 한 장의 메모리와 VM 이 그 모델로 가질 수 있는 장 수(`최대 n장`)다.
 * VM 이 그 모델로 한 장도 가질 수 없으면 잠긴 채 사유(`남은 카드 없음`·`이 노드에 없음`)를 단다. 쥔 모델은 늘 고를 수 있다.
 */
export function roomModelItem(m: RoomModel, held: string | undefined) {
  const locked = m.model !== held && m.max === 0
  const detail = [m.vramMb > 0 ? `한 장 ${sizeMb(m.vramMb)}` : '', m.onNode ? `최대 ${m.max}장` : ''].filter((x) => x !== '').join(' · ')
  return {
    value: m.model,
    title: m.model,
    detail: detail === '' ? undefined : detail,
    disabled: locked,
    reason: locked ? (m.onNode ? '남은 카드 없음' : '이 노드에 없음') : undefined,
  }
}
